import { useMemo } from 'react'
import { Sigma, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  aggregateRefKey,
  calculatedCellNodes,
  cellKey,
  columnCellKeys,
  formulaReachesItself,
  rowCellKeys,
  sumFormula,
  averageFormula,
} from '@/lib/tableForm'
import type { RefFormGroup } from './FormFieldRefSelect'
import { CrossFormHints, FormulaEditor, type FormulaOperand } from './FormulaEditor'
import {
  OPERAND_TYPES,
  buildCellsByKey,
  buildCrossLabels,
  buildRefGroups,
  buildTableSources,
  buildTotalLabels,
  initialTablePick,
  notApplicableRefs,
  tableRefKey,
} from './formulaRefPicker'
import { TableRefPicker } from './TableRefPicker'
import type { FormField } from './types'
import type { CrossFormAuthoring } from '@/hooks/useCrossFormAuthoring'
import type { TableCellConfig, TableCellType, TableConfig } from '@/types/cgt'

// Same split as FormulaBuilder: these feed arithmetic, the rest only join text.
const NUMERIC_FIELD_TYPES: FormField['type'][] = ['number', 'calculated']
const NUMERIC_CELL_TYPES: TableCellType[] = ['number', 'calculated']
// An image or description cell holds no value in the values map.
const VALUELESS_CELL_TYPES: TableCellType[] = ['image', 'description']

interface Props {
  config: TableConfig
  rowId: string
  colId: string
  value: TableCellConfig
  onChange: (patch: Partial<TableCellConfig>) => void
  /** Id of the table field being edited — tells "this table" apart from the form's other tables. */
  tableFieldId?: string
  /** This form's fields — other tables' cells and values outside the table. */
  allFields?: FormField[]
  /** Shown in the picker's form select ("This form — <name>"). */
  currentFormName?: string
  /** Cross-form reference authoring — forms earlier in the procedure template. */
  crossForm?: CrossFormAuthoring
}

/**
 * Formula editor for a calculated table cell. It is the same editor ordinary
 * calculated fields use (FormulaEditor) — IF/AND/OR, comparisons, SUM/MAX,
 * math and date functions, live preview — with references that also reach
 * cells: of this table or another table on the form (pick the table, then its
 * column and row → `{rowId__colId}`, or a column or row total →
 * `{tableId#col:colId:sum}`), besides fields on this form (`{fieldId}`) and
 * fields from earlier forms (`{handle.fieldId}`).
 */
export function CellFormulaBuilder({
  config,
  rowId,
  colId,
  value,
  onChange,
  tableFieldId,
  allFields,
  currentFormName,
  crossForm,
}: Props) {
  const formula = value.formula ?? ''
  const selfKey = cellKey(rowId, colId)

  // This table first (from the live `config`, which carries unsaved edits),
  // then every other table on the form.
  const tables = useMemo(
    () => buildTableSources(allFields, { fieldId: tableFieldId, config }),
    [config, tableFieldId, allFields],
  )

  // Every referenceable cell → its label ("Weight @ 2024", or "Vitals › Weight @
  // 2024" for another table), its resolved type and the table it belongs to.
  const cellsByKey = useMemo(() => buildCellsByKey(tables), [tables])
  // Column and row totals of every table field ("Sum of Weight (Vitals)").
  const totalLabels = useMemo(() => buildTotalLabels(tables), [tables])

  // Fields outside any table.
  const fieldsById = useMemo(
    () => new Map((allFields ?? []).filter(f => f.type !== 'table').map(f => [f.id, f])),
    [allFields],
  )
  const crossLabels = useMemo(() => buildCrossLabels(formula, crossForm), [formula, crossForm])
  const operandFields = useMemo(
    () => (allFields ?? []).filter(f => OPERAND_TYPES.includes(f.type)),
    [allFields],
  )

  // The same groups FormulaBuilder offers, with this form's tables listed
  // ahead of its fields.
  const refGroups = useMemo<RefFormGroup[]>(() => {
    const [self, ...upstream] = buildRefGroups({ operandFields, currentFormName, crossForm })
    const tableEntries = tables.map(t => ({
      refKey: tableRefKey(t),
      label: t.label,
      type: t.current ? undefined : 'table',
    }))
    return [{ ...self, fields: [...tableEntries, ...self.fields] }, ...upstream]
  }, [operandFields, currentFormName, crossForm, tables])

  const labels = useMemo(() => {
    const m: Record<string, string> = { ...crossLabels }
    for (const f of fieldsById.values()) m[f.id] = f.label || 'Untitled'
    for (const [key, cell] of cellsByKey) m[key] = cell.label
    return { ...m, ...totalLabels }
  }, [crossLabels, fieldsById, cellsByKey, totalLabels])

  // What the structured IF / SUM / MAX / date / math builders offer: every
  // cell that holds a value (not this one), the table totals, then this form's
  // fields and, for IF, the earlier forms' fields, exactly as FormulaBuilder does.
  const operands = useMemo<FormulaOperand[]>(
    () => [
      ...[...cellsByKey]
        .filter(
          ([key, cell]) => key !== selfKey && !cell.disabled && !VALUELESS_CELL_TYPES.includes(cell.type),
        )
        .map(([key, cell]) => ({ id: key, label: cell.label })),
      ...Object.entries(totalLabels).map(([id, label]) => ({ id, label })),
      ...operandFields.map(f => ({ id: f.id, label: f.label || 'Untitled' })),
    ],
    [cellsByKey, selfKey, totalLabels, operandFields],
  )
  const conditionOperands = useMemo<FormulaOperand[]>(
    () => [
      ...operands,
      ...(crossForm?.available && crossForm.selectedProcedureTemplateId
        ? crossForm.allUpstreamFields.map(f => ({ id: f.refKey, label: f.label }))
        : []),
    ],
    [operands, crossForm],
  )

  // Calculated cells and calculated fields are evaluated in one dependency
  // order at fill time, so a loop back to this cell, through other cells or
  // through a field, would leave the whole chain blank.
  const isCircular = useMemo(
    () =>
      formulaReachesItself(
        [
          ...(allFields ?? [])
            .filter(f => f.type === 'calculated')
            .map(f => ({ id: f.id, label: f.label, fieldType: 'calculated', formula: f.formula })),
          ...calculatedCellNodes(tables.map(t => ({ fieldType: 'table', tableConfig: t.config }))),
        ],
        selfKey,
      ),
    [allFields, tables, selfKey],
  )
  const naRefs = useMemo(() => notApplicableRefs(formula, cellsByKey), [formula, cellsByKey])

  // Quick totals. A table field reads a total token, which skips blank and not
  // applicable cells and covers rows added at fill time; a grid with no field
  // id has none, so it still writes the cells out one by one.
  const colKeys = columnCellKeys(config, colId, rowId)
  const rowKeys = rowCellKeys(config, rowId, colId)
  const aggregate = (axis: 'col' | 'row', op: 'sum' | 'avg') => {
    if (tableFieldId) {
      onChange({ formula: `{${aggregateRefKey(tableFieldId, axis, axis === 'col' ? colId : rowId, op)}}` })
      return
    }
    const keys = axis === 'col' ? colKeys : rowKeys
    const f = op === 'sum' ? sumFormula(keys) : averageFormula(keys)
    if (f) onChange({ formula: f })
  }

  const currentTableRef = tableRefKey(tables[0])
  // Any cell that holds a value, except this one.
  const cellAllowed = (key: string) => {
    const cell = cellsByKey.get(key)
    return !!cell && key !== selfKey && !cell.disabled && !VALUELESS_CELL_TYPES.includes(cell.type)
  }

  return (
    <FormulaEditor
      header={
        <div>
          <Label className='mb-1.5 flex items-center gap-1.5 text-xs'>
            <Sigma className='h-3.5 w-3.5' />
            Quick totals
          </Label>
          <div className='grid grid-cols-2 gap-1.5'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={colKeys.length === 0}
              onClick={() => aggregate('col', 'sum')}
            >
              Sum column
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={colKeys.length === 0}
              onClick={() => aggregate('col', 'avg')}
            >
              Avg column
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={rowKeys.length === 0}
              onClick={() => aggregate('row', 'sum')}
            >
              Sum row
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={rowKeys.length === 0}
              onClick={() => aggregate('row', 'avg')}
            >
              Avg row
            </Button>
          </div>
          <p className='mt-1 text-[11px] text-muted-foreground'>
            Totals add up the number cells in this column or row. Empty cells and cells marked
            not applicable are left out.
          </p>
        </div>
      }
      formula={formula}
      onFormulaChange={f => onChange({ formula: f })}
      displayFormat={value.displayFormat}
      defaultDisplayFormat='decimal2'
      onDisplayFormatChange={displayFormat => onChange({ displayFormat })}
      labels={labels}
      isCrossRef={id => crossLabels[id] !== undefined}
      isNumericRef={id => {
        const cell = cellsByKey.get(id)
        if (cell) return NUMERIC_CELL_TYPES.includes(cell.type)
        if (totalLabels[id] !== undefined) return true
        return NUMERIC_FIELD_TYPES.includes(fieldsById.get(id)?.type ?? 'text')
      }}
      operands={operands}
      conditionOperands={conditionOperands}
      emptyHint='Use a quick total, or add a reference below…'
      brokenLabel='⚠ deleted reference'
      refNoun={['cell or field', 'cells or fields']}
      hasExtraError={isCircular || naRefs.length > 0}
      extraWarnings={
        <>
          {isCircular && (
            <div className='flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive'>
              <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
              <span>
                This formula refers back to this cell through other calculated cells or fields, so
                it can never be worked out. Remove one of the references in the loop.
              </span>
            </div>
          )}
          {naRefs.length > 0 && <NotApplicableWarning labels={naRefs} />}
        </>
      }
      renderRefEditor={(id, replace) => (
        <TableRefPicker
          groups={refGroups}
          tables={tables}
          initial={initialTablePick(id, tables, labels[id] !== undefined ? id : currentTableRef)}
          cellAllowed={cellAllowed}
          actionLabel='Apply'
          size='sm'
          onPick={replace}
          formula={formula}
        />
      )}
      renderReferencePicker={insert => (
        // One picker for every reference: a table cell (this table or another),
        // a field on this form, or a field from an earlier form.
        <div className='space-y-1.5'>
          <p className='text-xs text-muted-foreground'>Add a cell or field to the formula:</p>
          <TableRefPicker
            groups={refGroups}
            tables={tables}
            initial={{ ref: currentTableRef }}
            cellAllowed={cellAllowed}
            actionLabel='Insert'
            onPick={insert}
            formula={formula}
          />
          <CrossFormHints crossForm={crossForm} hasUpstream={refGroups.length > 1} />
        </div>
      )}
    />
  )
}

/** A formula that reads a cell marked not applicable, which never holds a value. */
export function NotApplicableWarning({ labels }: { labels: string[] }) {
  return (
    <div className='flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive'>
      <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
      <span>
        This formula uses {labels.length === 1 ? 'a cell' : 'cells'} marked not applicable, which
        never {labels.length === 1 ? 'has a value' : 'have a value'}: {labels.join(', ')}. Remove{' '}
        {labels.length === 1 ? 'it' : 'them'} from the formula.
      </span>
    </div>
  )
}
